import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatAIText } from "@/lib/aiFormat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getSessionId = () => {
  let sid = localStorage.getItem("pricelens_ai_session");
  if (!sid) {
    sid = `sess-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem("pricelens_ai_session", sid);
  }
  return sid;
};

const SUGGESTIONS = [
  "Which competitors dropped prices this week?",
  "Which of our products are below BAU?",
  "Any hidden markdowns I should know about?",
];

export const AskAI = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    if (open && messages.length === 0) loadHistory();
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  const loadHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/ai/chat/${sessionId.current}/history`);
      if (data.messages?.length) {
        setMessages(data.messages.map((m) => ({ role: m.role, content: m.content })));
      }
    } catch (e) {
      console.error("Failed to load chat history:", e);
    }
  };

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: msg }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const ev = JSON.parse(line.slice(6));
          if (ev.type === "delta") {
            setMessages((prev) => {
              const next = [...prev];
              next[next.length - 1] = {
                role: "assistant",
                content: next[next.length - 1].content + ev.content,
              };
              return next;
            });
          } else if (ev.type === "error") {
            throw new Error(ev.detail);
          }
        }
      }
    } catch (e) {
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = {
          role: "assistant",
          content: "Sorry, I couldn't answer that right now. Please try again.",
        };
        return next;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          data-testid="ask-ai-fab"
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-50 flex items-center gap-2 px-4 py-3 rounded-full bg-[#1a1814] text-white shadow-xl hover:bg-[#2d2820] transition-colors border border-[#f0a500]/40"
        >
          <Sparkles className="h-4 w-4 text-[#f0a500]" />
          <span className="text-sm font-bold">Ask AI</span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="ask-ai-panel"
          className="fixed bottom-5 right-5 z-50 w-[380px] max-w-[calc(100vw-2rem)] h-[540px] max-h-[calc(100vh-6rem)] bg-white rounded-2xl shadow-2xl border border-[#e0dbd2] flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-[#1a1814] text-white flex-shrink-0">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-[#f0a500]" />
              <div>
                <p className="text-sm font-extrabold">Ask AI</p>
                <p className="text-[10px] text-white/50">Claude · live pricing data</p>
              </div>
            </div>
            <button
              data-testid="ask-ai-close"
              onClick={() => setOpen(false)}
              className="p-1.5 rounded-md hover:bg-white/10"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#f8f5ee]">
            {messages.length === 0 && (
              <div className="space-y-3">
                <p className="text-sm text-[#8a8278]">
                  Ask me anything about your tracked prices, competitors, BSR or markdowns.
                </p>
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    data-testid={`ai-suggestion-${i}`}
                    onClick={() => send(s)}
                    className="block w-full text-left text-xs px-3 py-2.5 rounded-lg bg-white border border-[#e0dbd2] hover:border-[#f0a500] hover:bg-[#fffaf0] transition-colors text-[#1a1814]"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={cn("flex", m.role === "user" ? "justify-end" : "justify-start")}>
                <div
                  className={cn(
                    "max-w-[85%] rounded-xl px-3.5 py-2.5 text-[13px] leading-relaxed",
                    m.role === "user"
                      ? "bg-[#1a1814] text-white"
                      : "bg-white border border-[#e0dbd2] text-[#1a1814]"
                  )}
                >
                  {m.role === "assistant" ? (
                    m.content ? (
                      <div dangerouslySetInnerHTML={{ __html: formatAIText(m.content) }} />
                    ) : (
                      <span className="inline-flex gap-1">
                        <span className="w-1.5 h-1.5 bg-[#f0a500] rounded-full animate-bounce" />
                        <span className="w-1.5 h-1.5 bg-[#f0a500] rounded-full animate-bounce [animation-delay:0.15s]" />
                        <span className="w-1.5 h-1.5 bg-[#f0a500] rounded-full animate-bounce [animation-delay:0.3s]" />
                      </span>
                    )
                  ) : (
                    m.content
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Input */}
          <div className="p-3 border-t border-[#e0dbd2] bg-white flex gap-2 flex-shrink-0">
            <input
              data-testid="ask-ai-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask about prices, BSR, competitors..."
              disabled={streaming}
              className="flex-1 text-sm px-3 py-2 rounded-lg bg-[#f2f0ec] border border-[#e0dbd2] focus:outline-none focus:ring-2 focus:ring-[#f0a500]/50"
            />
            <button
              data-testid="ask-ai-send"
              onClick={() => send()}
              disabled={streaming || !input.trim()}
              className="px-3.5 rounded-lg bg-[#f0a500] hover:bg-[#ffc533] disabled:opacity-40 text-[#1a1200] transition-colors"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};
